/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable max-len */
import { useQuery } from '@apollo/client';
import Close from '@mui/icons-material/Close';
import Menu from '@mui/icons-material/Menu';
import cx from 'classnames';
import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useAuthentication from '../hooks/useAuthentication';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import goOilLogo from '../images/gooil-logo.png';
import styles from '../sass/components/NavBar.module.scss';
import {
  ROUTE_ACCOUNT_ROOT,
  ROUTE_AUTH_LOGIN,
  ROUTE_BOOK_LOCATION,
  ROUTE_BOOK_ROOT,
  ROUTE_FRANCHISE_MANAGER_WORK_ORDERS,
  ROUTE_ROOT,
} from '../util/constants';
import { GET_FRANCHISES_OF_FRANCHISE_MANAGER } from '../util/gql';
import {
  setFrnchiseId,
  setFranchiseName,
  setFranchiseType,
  setFranchiseCountryCode,
  setInventoryFeature,
} from '../features/franchise/franchiseSlice';
import { updateFranchiseId } from '../features/auth/authSlice';
import { Franchise, Feature } from '../interfaces/FranchiseNavBar';
import { useTenant } from './TenantContext';
import { normalizeThemePreset } from '../util/themePreset';
import { marketingBase } from '../util/marketingSite';
import AperioSiteHeader from './AperioSiteHeader';
import { siteFirstName } from '../util/siteSession';

export default function NavBar() {
  const currentRoutePath = useLocation().pathname;
  const navigate = useNavigate();
  const { franchiseId } = useAppSelector((state) => state.auth.currentUser);
  const franchiseIdSelect = useAppSelector((state) => state.franchise.id);
  const franchiseNameSelect = useAppSelector((state) => state.franchise.name);
  const [navOpen, setNavOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  // The same keyboard rule as the preset's menu (QA 2026-09-25 #47): Escape
  // closes the open menu and puts focus back on its button.
  useEffect(() => {
    if (!navOpen) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setNavOpen(false);
      menuButtonRef.current?.focus();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [navOpen]);
  const { loggedIn: isLoggedIn, handleLogin } = useAuthentication();
  const currentUser = useAppSelector((state) => state.auth.currentUser);
  const hasRole = currentUser.roles?.includes('manager')
    || currentUser.roles?.includes('technician')
    || currentUser.roles?.includes('tester')
    || currentUser.roles?.includes('sales');
  const dispatch = useAppDispatch();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [franchises, setFranchises] = useState<Franchise[]>([]);
  const [hasFetched, setHasFetched] = useState(false);
  const shouldRun = isLoggedIn && hasRole && !hasFetched;

  const { data } = useQuery(GET_FRANCHISES_OF_FRANCHISE_MANAGER, {
    fetchPolicy: 'network-only',
    skip: !shouldRun,
  });

  const tenant = useTenant();
  const logoSrc = tenant?.logoUrl;

  useEffect(() => {
    if (data && !hasFetched) {
      if (data?.getFranchisesOfFranchiseManager) {
        const formattedFranchises = data.getFranchisesOfFranchiseManager.map((franchise: Franchise) => {
          const inventoryFeature = franchise.feature.find(
            (feature: Feature) => feature.name === 'inventory',
          );

          return {
            id: franchise.id,
            name: franchise.name,
            type: franchise.type,
            countryCode: franchise.address.countryCode,
            inventory: inventoryFeature ? inventoryFeature.active : true,
          };
        });
        setFranchises(formattedFranchises);
      }
      setHasFetched(true);
    }
  }, [data, hasFetched]);

  useEffect(() => {
    if (isLoggedIn && hasRole) {
      setHasFetched(false);
    }
  }, [currentUser?.id, isLoggedIn, hasRole]);

  useEffect(() => {
    if (!isLoggedIn || !hasRole) {
      setFranchises([]);
      setHasFetched(false);
    }
  }, [isLoggedIn, hasRole]);

  const setFranchise = (id: number, name: string, type: number, countryCode: string, inventory: boolean) => {
    dispatch(setFrnchiseId(id));
    dispatch(setFranchiseName(name));
    dispatch(setFranchiseType(type));
    dispatch(setFranchiseCountryCode(countryCode));
    dispatch(updateFranchiseId(id));
    dispatch(setInventoryFeature(inventory));
  };

  const handleFranchiseSelection = (id: number, name: string, type: number, countryCode: string, inventory: boolean) => {
    setFranchise(id, name, type, countryCode, inventory);
    navigate(ROUTE_FRANCHISE_MANAGER_WORK_ORDERS);
  };

  useEffect(() => {
    if ((franchiseIdSelect !== franchiseId) && franchises) {
      const currentFranchise = franchises.find(
        (franchise: Franchise) => franchise.id === franchiseId,
      );
      if (currentFranchise) {
        setFranchise(franchiseId, currentFranchise.name, currentFranchise.type, currentFranchise.countryCode, currentFranchise.inventory);
      }
    }
  }, [franchiseIdSelect, franchiseId, franchises]);

  // A tenant on a marketing-site theme preset gets that site's own header
  // instead of this one, so the bar does not change across the hand-off. Every
  // other tenant - Go Oil included - falls straight through to the markup
  // below, unchanged.
  const marketingUrl = marketingBase(tenant?.marketingSiteUrl);
  const themed = normalizeThemePreset(tenant?.themePreset) !== 'default';
  if (marketingUrl && themed) {
    return (
      <AperioSiteHeader
        marketingUrl={marketingUrl}
        isLoggedIn={isLoggedIn}
        fleetAccount={isLoggedIn && !!currentUser.roles?.includes('fleet_manager')}
        onLogOut={handleLogin}
        accountName={isLoggedIn ? siteFirstName(currentUser.name) : ''}
        franchises={isLoggedIn && hasRole && franchises ? franchises : []}
        franchiseName={franchiseNameSelect}
        onSelectFranchise={(franchise: Franchise) => handleFranchiseSelection(
          franchise.id,
          franchise.name,
          franchise.type,
          franchise.countryCode,
          franchise.inventory,
        )}
      />
    );
  }

  return (
    <nav className={cx(styles.navBar, {
      [styles.navBarClosed]: !navOpen,
    }, {
      [styles.navBarOpen]: navOpen,
    })}
    >
      <Link
        className={styles.logo}
        to={ROUTE_ROOT}
        onClick={() => setNavOpen(false)}
      >
        {logoSrc ? (
          <img src={logoSrc} alt="Tenant logo" className={styles.logoSvg} />
        ) : (
          <img src={goOilLogo} alt="Go oil logo" className={styles.logoSvg} />
        )}
      </Link>
      <div
        id="navbar-links"
        className={cx(styles.navLinks, {
          [styles.navLinksClosed]: !navOpen,
        }, {
          [styles.navLinksOpen]: navOpen,
        })}
      >
        {isLoggedIn && hasRole && franchises && (
          <div
            className={cx(styles.dropdownContainer, {
              [styles.navLinksClosed]: !navOpen,
              [styles.navLinksOpen]: navOpen,
            })}
          >
            <button
              type="button"
              onClick={() => setDropdownOpen((prev) => !prev)}
              className={cx(styles.dropdownTrigger, {
                [styles.linkClosed]: !navOpen,
                [styles.linkOpen]: navOpen,
              })}
            >
              <h4>{franchiseNameSelect || 'Select a Franchise'}</h4>
            </button>
            {dropdownOpen && (
              <ul className={styles.dropdownList}>
                {franchises.map((franchise: Franchise) => (
                  <button
                    key={franchise.id}
                    type="button"
                    className={styles.dropdownItem}
                    onClick={() => {
                      handleFranchiseSelection(franchise.id, franchise.name, franchise.type, franchise.countryCode, franchise.inventory);
                      setDropdownOpen(false);
                    }}
                  >
                    {franchise.name}
                  </button>
                ))}
              </ul>
            )}
          </div>
        )}

        <Link
          className={cx(styles.link, {
            [styles.linkClosed]: !navOpen,
          }, {
            [styles.linkOpen]: navOpen,
          }, {
            [styles.currentPage]: currentRoutePath.includes(ROUTE_BOOK_ROOT),
          })}
          to={ROUTE_BOOK_LOCATION}
          onClick={() => {
            setNavOpen(false);
            setDropdownOpen(false);
          }}
        >
          BOOK APPOINTMENT
        </Link>
        <Link
          className={cx(styles.link, {
            [styles.linkClosed]: !navOpen,
          }, {
            [styles.linkOpen]: navOpen,
          }, {
            [styles.currentPage]: currentRoutePath.includes(ROUTE_ACCOUNT_ROOT),
          })}
          to={isLoggedIn ? ROUTE_ACCOUNT_ROOT : ROUTE_AUTH_LOGIN}
          onClick={() => {
            setNavOpen(false);
            setDropdownOpen(false);
          }}
        >
          ACCOUNT
        </Link>
        {isLoggedIn && navOpen && (
          <Link
            className={cx(styles.link, {
              [styles.linkOpen]: navOpen,
            })}
            to={ROUTE_AUTH_LOGIN}
            onClick={handleLogin}
          >
            LOG OUT
          </Link>
        )}
        <button
          type="button"
          ref={menuButtonRef}
          // Icon-only: it had no name at all (QA 2026-09-25 #29/#47).
          aria-label={navOpen ? 'Close menu' : 'Menu'}
          aria-expanded={navOpen}
          aria-controls="navbar-links"
          className={cx(styles.button, {
            [styles.menuButton]: !navOpen,
          }, {
            [styles.closeButton]: navOpen,
          })}
          onClick={() => {
            setNavOpen(!navOpen);
            setDropdownOpen(false);
          }}
        >
          {navOpen ? <Close /> : <Menu />}
        </button>
      </div>
    </nav>
  );
}
